import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import CustomInput from '../CommonComp/CustomInput/CustomInput';
import CustomButton from '../CommonComp/Buttons/CustomButton';
import { addImageAdminUser } from '../../Services/userApis';
import { imageListingsNoRedux } from '../../Services/imageApis';
import CustomAsyncSelect from '../CommonComp/CustomInput/CustomAsyncSelect';
import CloseIcon from '@mui/icons-material/Close';
import CustomIconButtons from '../CommonComp/Buttons/IconButtons';
import CustomImage from '../CommonComp/CustomImage/CustomImage';
import ApiConfig from '../../config/ApiConfig';
import { action, logsMessagesList, modulesNames } from '../../data/logsMessages';
import { adminAddLogs } from '../../Services/commonService';


function AddImagesPackModal({ open, handleOpen, heading, getActiveIvsData, upgradeData }) {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('image');

  const getImageListing = async (inputValue) => {
    if (inputValue && inputValue.length > 1) {
      let data = await imageListingsNoRedux({ search: inputValue, type: type });
      if (type === 'image') {
        let updatedData = data?.map((item) => ({
          value: item.Name,
          label: item.Name,
          rank: item.rank,
          sno: item.sno,
          shootid: item.shootid,
          image_name: item.Name,
          price: null,
          quality: null,
        }));
        return updatedData || [];
      } else if (type === 'video') {
        let updatedData = data?.map((item) => ({
          value: item.video_name,
          label: item.video_name,
          rank: item.rank,
          sno: item.video_id,
          shootid: item.shootid,
          image_name: item.video_name,
          price: null,
          quality: null,
        }));
        return updatedData || [];
      }
      return [];
    }
  };

  const handleImageDownload = async (values) => {
    if (!upgradeData?.order_id) return;
    try {
      setLoading(true);
      const resp = await addImageAdminUser({
        order_id: upgradeData.order_id,
        image_name: values?.image?.image_name,
        client: values?.client_name,
        type: type === 'image' ? '1' : '2',
      });
      if (resp) {
        handleOpen(false);
        getActiveIvsData();
        let payloadForLogs = {
          action: action?.add,
          module: modulesNames?.users,
          description: logsMessagesList?.users?.userImageAdd,
        };
        await adminAddLogs(payloadForLogs);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const handleType = (val) => {
    setType(val);
    formik.setFieldValue('image', '');
  };

  const formik = useFormik({
    initialValues: {
      image: '',
      client_name: '',
    },
    validationSchema: Yup.object({
      image: Yup.object().required('Please select an image or video'),
      client_name: Yup.string().required('Please enter the Client/Designated End User name'),
    }),
    onSubmit: handleImageDownload,
  });

  useEffect(() => {
    if (upgradeData) {
      formik.setFieldValue('client_name', upgradeData.client || '');
    }
  }, [upgradeData]);

  const getDisplayRemaining = () => {
    if (upgradeData?.unlimited_pack === '1') {
      return 'UNLIMITED';
    }
    const total = upgradeData?.total_images;
    const used = upgradeData?.remaining_images;

    if (typeof total === 'number' && typeof used === 'number') {
      if (total === used) {
        return used;
      }
      else {
        return used;
      }
    }
    return 'N/A';
  };

  return (
    <div>
      <Dialog
        maxWidth={'lg'}
        open={open}
        fullWidth
        onClose={() => {
          if (!loading) handleOpen(false);
        }}
      >
        <DialogTitle component="div" padding={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h3">{heading}</Typography>
          {handleOpen && (
            <CustomIconButtons onClick={() => handleOpen(false)} sx={{ padding: '13px 15px', ml: 'auto' }} color={'iconButtonPrimary'} disabled={loading}>
              <CloseIcon />
            </CustomIconButtons>
          )}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ minWidth: 300, height: '100%' }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomInput
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ classes: { notchedOutline: 'rounded' } }}
                      select
                      SelectProps={{ native: true, style: { height: 40 } }}
                      name="type"
                      label="Type"
                      value={type}
                      onChange={(e) => handleType(e?.target.value)}
                      style={{ maxWidth: 150 }}
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </CustomInput>
                  </Grid>

                  <Grid item xs={12}>
                    <CustomAsyncSelect
                      key={type}
                      promiseOptions={getImageListing}
                      closeMenuOnSelect={false}
                      controlStyle={{ padding: 5 }}
                      cacheOptions={true}
                      handleChange={(e) => formik.setFieldValue('image', e)}
                      width={'100%'}
                      dropDownZIndex={100000}
                      labelShrink={true}
                      label={type === 'image' ? 'Search Image' : 'Search Video'}
                      isMulti={false}
                      error={formik.touched.image && Boolean(formik.errors.image)}
                      helperText={formik.touched.image && formik.errors.image}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box className="border-1 rounded-3" mt={2} px={2} py={1} minHeight={150}>
                      <Typography variant="h6">Preview</Typography>
                      {formik?.values?.image ? (
                        <Box sx={{ display: 'flex', overflow: 'hidden', overflowX: 'auto' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 175, marginRight: 2, marginBottom: 2, bgcolor: (theme) => theme.backgroundColor.box.primary, px: 0.5, pb: 1, borderRadius: 5, position: 'relative' }}>
                            <CustomImage
                              src={`${ApiConfig.BASE_IMAGE_URL}${formik?.values?.image?.shootid}/${formik?.values?.image?.rank}-${formik?.values?.image?.image_name}.jpg`}
                              style={{ maxWidth: 170, marginTop: 5, maxHeight: 110, borderRadius: 10, width: 165, height: 110 }}
                            />
                            <Typography variant="h6" mt={1} pl={1}>{formik?.values?.image?.image_name}</Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body1" component={'p'} className="fontWeight500" textAlign={'center'} pt={4}>
                          No Data Available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography variant="h4" mb={2}>Selected Order Details</Typography>
                <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Order ID:</Typography>
                    <Typography variant="body1">{upgradeData?.order_id || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Invoice ID:</Typography>
                    <Typography variant="body1">{upgradeData?.invoice_id || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Package:</Typography>
                    <Typography variant="body1">{upgradeData?.size || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Validity:</Typography>
                    <Typography variant="body1">{upgradeData?.expired_on || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Remaining:</Typography>
                    <Typography 
                      variant="body1" 
                      color={upgradeData?.unlimited_pack === '1' ? 'success.main' : 'inherit'} 
                      fontWeight="bold"
                    >
                      {getDisplayRemaining()}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: 3 }}>
                  <CustomInput
                    name="client_name"
                    label="Client/Designated End User"
                    placeholder="Client/Designated End User"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ classes: { notchedOutline: 'rounded' } }}
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.client_name}
                    error={formik.touched.client_name && Boolean(formik.errors.client_name)}
                    helperText={formik.touched.client_name && formik.errors.client_name}
                  />
                  <CustomButton loading={loading} type="submit" sx={{ padding: '14px 15px', marginLeft: 2, mt: 0.5,  whiteSpace: 'nowrap' }} color={'buttonSecondary'}>
                    {type === 'image' ? 'Add Images' : 'Add Videos'}
                  </CustomButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AddImagesPackModal;